import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { articleCategories } from '@/mocks/categories';

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || 'all';

  const handleCategoryChange = (catId: string) => {
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: catId });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  const activeCatName = articleCategories.find(c => c.id === activeCategory)?.name || '全部文章';

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCatName} - 影视资讯 | 麻豆精品影视网`,
    description: '麻豆精品影视网影视资讯板块，提供最新影视新闻、深度影评和独家专访内容',
    url: `https://www.madoujipin.com/articles${activeCategory !== 'all' ? `?cat=${activeCategory}` : ''}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10 md:pb-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '影视资讯' },
            ]}
          />

          <div className="mt-4 md:mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              影视资讯
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              最新影视新闻、深度影评与独家专访
            </p>
          </div>

          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-2">
            {articleCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs font-medium rounded whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.publishDate}</span>
                  </div>
                  <h2 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                    <span>
                      <i className="ri-eye-line mr-1"></i>
                      {article.readCount}阅读
                    </span>
                    <span>
                      <i className="ri-user-line mr-1"></i>
                      {article.author}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16 md:py-20">
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}